/**
 * Transforms timeseries data from the /timeseries API endpoint into line chart format
 *
 * @param {Array} responseArray - Array of timeseries objects from API
 * @param {string} entityType - Type of entity ('metrics' or 'hosts')
 * @returns {Object|null} - Transformed data with categories and series, or null if no data
 */
export const transformTimeseriesData = (responseArray, entityType = 'metrics') => {
  if (!Array.isArray(responseArray) || responseArray.length === 0) {
    return null;
  }

  const timestampsSet = new Set();
  responseArray.forEach((item) => {
    const timeseries = item.timeseries || {};
    Object.keys(timeseries).forEach((seriesName) => {
      const seriesData = timeseries[seriesName] || [];
      seriesData.forEach((point) => {
        if (point && point.timestamp) {
          timestampsSet.add(point.timestamp);
        }
      });
    });
  });

  const timestamps = Array.from(timestampsSet).sort();
  if (timestamps.length === 0) {
    return null;
  }

  const series = [];
  responseArray.forEach((item) => {
    const metricName = item.metric_name || item.name || 'Unknown';
    const hostName = item.host_name || item.hostName || '';
    const muid = item.muid || '';

    const timeseries = item.timeseries || {};
    Object.keys(timeseries).forEach((seriesKey) => {
      const seriesData = timeseries[seriesKey] || [];

      const valueMap = {};
      seriesData.forEach((point) => {
        if (point && point.timestamp != null && point.value != null) {
          valueMap[point.timestamp] = point.value;
        }
      });

      const data = timestamps.map((ts) => valueMap[ts] ?? null);

      let displayName = seriesKey;
      if (entityType === 'metrics' && hostName && metricName) {
        if (Object.keys(timeseries).length === 1) {
          displayName = `${hostName} - ${metricName}`;
        } else {
          displayName = `${hostName} - ${metricName} [${seriesKey}]`;
        }
      } else if (entityType === 'hosts') {
        displayName = `${hostName} [${seriesKey}]`;
      }

      series.push({
        name: displayName,
        data,
        muid,
      });
    });
  });

  const categories = timestamps.map((ts) => {
    const date = new Date(ts * 1000);
    return date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  });

  return {
    categories,
    series,
  };
};
